import React from 'react';
import { motion } from 'framer-motion';

export default function Hero() {
  return (
    <section className="relative pt-40 pb-24 md:pt-56 md:pb-32 overflow-hidden min-h-[90vh] flex items-center">
      <div
        className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat opacity-20"
        style={{ backgroundImage: "url('https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=1920&q=80')" }}
      />
      <div className="absolute inset-0 z-0 bg-gradient-to-b from-black/90 via-black/80 to-brand-dark" />
      <div
        className="absolute inset-0 z-0 opacity-5"
        style={{
          backgroundImage: 'linear-gradient(rgba(232,101,26,0.4) 1px,transparent 1px),linear-gradient(90deg,rgba(232,101,26,0.4) 1px,transparent 1px)',
          backgroundSize: '60px 60px'
        }}
      />
      <div className="container mx-auto px-8 relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-16">
          <motion.div
            className="w-full lg:w-1/2 text-center lg:text-left"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-block py-1.5 px-4 rounded-full bg-brand-orange/10 border border-brand-orange/30 text-brand-orange text-sm font-semibold mb-8 tracking-wider uppercase">
              Secure Your Future Today
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-8 text-white">
              Minotaur Solutions <br />
              <span className="text-brand-orange">Your Shield</span> in the <br />
              Cybersecurity Maze
            </h1>
            <p className="text-lg text-gray-300 mb-10 max-w-xl mx-auto lg:mx-0 leading-relaxed">
              In today's digital world, cyber threats and compliance demands are growing faster than ever.
              We make cybersecurity and data management simple, effective, and stress-free.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <a href="#contact" className="btn-primary">Get Your Free Consultation</a>
              <a href="#services" className="btn-outline">Explore Services</a>
            </div>
          </motion.div>
          <motion.div
            className="w-full lg:w-1/2 relative"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] bg-brand-orange/10 blur-[100px] rounded-full -z-10" />
            <HeroGraphic />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function HeroGraphic() {
  return (
    <div className="relative w-full max-w-lg mx-auto">
      <svg viewBox="0 0 500 500" className="w-full h-auto drop-shadow-2xl" style={{ animation: 'float 6s ease-in-out infinite' }}>
        <style>{`
          @keyframes float{0%,100%{transform:translateY(0)}50%{transform:translateY(-18px)}}
          @keyframes spin-slow{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
          @keyframes spin-rev{from{transform:rotate(0deg)}to{transform:rotate(-360deg)}}
        `}</style>
        <circle cx="250" cy="250" r="220" fill="none" stroke="rgba(232,101,26,0.08)" strokeWidth="1" />
        <circle cx="250" cy="250" r="190" fill="none" stroke="rgba(232,101,26,0.12)" strokeWidth="1" />
        <g style={{ transformOrigin: '250px 250px', animation: 'spin-slow 20s linear infinite' }}>
          <circle cx="250" cy="250" r="170" fill="none" stroke="rgba(232,101,26,0.25)" strokeWidth="1.5" strokeDasharray="8 6" />
        </g>
        <g style={{ transformOrigin: '250px 250px', animation: 'spin-rev 15s linear infinite' }}>
          <circle cx="250" cy="250" r="145" fill="none" stroke="rgba(232,101,26,0.2)" strokeWidth="1" strokeDasharray="4 10" />
        </g>
        {[0, 72, 144, 216, 288].map((angle, i) => {
          const rad = (angle * Math.PI) / 180;
          const x = 250 + 170 * Math.cos(rad);
          const y = 250 + 170 * Math.sin(rad);
          return (
            <g key={i}>
              <circle cx={x} cy={y} r="8" fill="#e8651a" opacity="0.9" />
              <circle cx={x} cy={y} r="14" fill="none" stroke="#e8651a" strokeWidth="1" opacity="0.4" />
            </g>
          );
        })}
        <path d="M250 90 L340 130 L340 220 Q340 310 250 360 Q160 310 160 220 L160 130 Z" fill="url(#shieldGrad)" stroke="rgba(232,101,26,0.6)" strokeWidth="2" />
        <path d="M250 110 L325 145 L325 215 Q325 295 250 340 Q175 295 175 215 L175 145 Z" fill="url(#shieldInner)" opacity="0.6" />
        <rect x="225" y="215" width="50" height="38" rx="6" fill="rgba(232,101,26,0.9)" />
        <path d="M237 215 L237 205 Q237 185 250 185 Q263 185 263 205 L263 215" fill="none" stroke="rgba(232,101,26,0.9)" strokeWidth="5" strokeLinecap="round" />
        <circle cx="250" cy="234" r="6" fill="#0a0a0a" />
        {[[250,90,250,60],[340,175,375,155],[160,175,125,155]].map(([x1,y1,x2,y2],i)=>(
          <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="rgba(232,101,26,0.4)" strokeWidth="1.5" strokeDasharray="4 3" />
        ))}
        <g><rect x="50" y="120" width="80" height="28" rx="14" fill="rgba(232,101,26,0.15)" stroke="rgba(232,101,26,0.5)" strokeWidth="1" /><text x="90" y="139" textAnchor="middle" fill="#e8651a" fontSize="10" fontFamily="Lexend,sans-serif" fontWeight="600">ISO 27001</text></g>
        <g><rect x="370" y="120" width="68" height="28" rx="14" fill="rgba(232,101,26,0.15)" stroke="rgba(232,101,26,0.4)" strokeWidth="1" /><text x="404" y="139" textAnchor="middle" fill="#e8651a" fontSize="10" fontFamily="Lexend,sans-serif" fontWeight="600">NIS2</text></g>
        <g><rect x="30" y="280" width="68" height="28" rx="14" fill="rgba(232,101,26,0.15)" stroke="rgba(232,101,26,0.5)" strokeWidth="1" /><text x="64" y="299" textAnchor="middle" fill="#e8651a" fontSize="10" fontFamily="Lexend,sans-serif" fontWeight="600">GDPR</text></g>
        <g><rect x="400" y="280" width="68" height="28" rx="14" fill="rgba(232,101,26,0.15)" stroke="rgba(232,101,26,0.4)" strokeWidth="1" /><text x="434" y="299" textAnchor="middle" fill="#e8651a" fontSize="10" fontFamily="Lexend,sans-serif" fontWeight="600">DORA</text></g>
        <g><rect x="180" y="390" width="68" height="28" rx="14" fill="rgba(232,101,26,0.15)" stroke="rgba(232,101,26,0.5)" strokeWidth="1" /><text x="214" y="409" textAnchor="middle" fill="#e8651a" fontSize="10" fontFamily="Lexend,sans-serif" fontWeight="600">NIST</text></g>
        <g><rect x="260" y="390" width="68" height="28" rx="14" fill="rgba(232,101,26,0.15)" stroke="rgba(232,101,26,0.4)" strokeWidth="1" /><text x="294" y="409" textAnchor="middle" fill="#e8651a" fontSize="10" fontFamily="Lexend,sans-serif" fontWeight="600">vCISO</text></g>
        <defs>
          <linearGradient id="shieldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="rgba(232,101,26,0.3)" />
            <stop offset="100%" stopColor="rgba(232,101,26,0.1)" />
          </linearGradient>
          <radialGradient id="shieldInner" cx="50%" cy="30%" r="60%">
            <stop offset="0%" stopColor="rgba(232,101,26,0.35)" />
            <stop offset="100%" stopColor="rgba(232,101,26,0)" />
          </radialGradient>
        </defs>
      </svg>
    </div>
  );
}